/**
 * The board's plugin overlay seam — renders every registered board-layer
 * contribution inside a space's edge SVG, each isolated by an error boundary so
 * a crashing plugin layer degrades to absent rather than taking down the board.
 *
 * The board itself holds NO integration code: it renders an ordered list of
 * opaque layers from the plugin registry. The live-traffic particle overlay is
 * one such contribution (mounted by the OTLP plugin), not board code. Absent a
 * plugin host, the registry is empty and this renders nothing.
 */
import { memo } from "react";
import type { BoardEdge, BoardItem, PlannedRoute } from "@archlang/scene/board";
import { useBoardLayerContributions } from "../workbench/plugins/PluginRegistryContext.js";
import { PluginBoundary } from "../workbench/plugins/WorkbenchPluginHost.js";

function BoardOverlayLayersImpl({
  edges,
  routes,
  nodes,
}: {
  readonly edges: readonly BoardEdge[];
  readonly routes: ReadonlyMap<string, PlannedRoute>;
  readonly nodes: ReadonlyMap<string, BoardItem>;
}) {
  const layers = useBoardLayerContributions();
  if (layers.length === 0) return null;
  return (
    <>
      {layers.map(({ pluginId, contribution }) => {
        const { Layer } = contribution;
        return (
          <PluginBoundary key={`${pluginId}:${contribution.id}`} pluginId={pluginId}>
            <Layer edges={edges} routes={routes} nodes={nodes} />
          </PluginBoundary>
        );
      })}
    </>
  );
}

export const BoardOverlayLayers = memo(BoardOverlayLayersImpl);
